import React, {
  useRef,
  useCallback,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  type MatrixWidgetProps,
  type MatrixThemingProps,
  MATRIX_THEMING_PROP_TO_PARAM,
} from "./types";

/**
 * Ref handle exposed by LegionMatrix for imperative control.
 */
export interface LegionMatrixHandle {
  /** Reload the widget iframe */
  refresh: () => void;
}

/**
 * Build the iframe src URL from widget props.
 */
export function buildMatrixWidgetUrl(props: MatrixWidgetProps): string {
  const {
    baseUrl,
    theme = "light",
    emptyTitle = "You're all caught up",
    emptySubtitle = "No updates right now",
    accentColor,
    emptyIcon = "bell",
  } = props;

  const url = new URL("/widget/matrix", baseUrl);
  url.searchParams.set("theme", theme);
  url.searchParams.set("emptyTitle", emptyTitle);
  url.searchParams.set("emptySubtitle", emptySubtitle);

  // Default accent color depends on theme
  const resolvedAccentColor =
    accentColor ?? (theme === "dark" ? "gray" : "blue");
  url.searchParams.set("accentColor", resolvedAccentColor);

  // Validate emptyIcon
  const validIcons: Array<MatrixWidgetProps["emptyIcon"]> = [
    "bell",
    "gift",
    "activity",
    "trophy",
  ];
  const resolvedIcon = validIcons.includes(emptyIcon) ? emptyIcon : "bell";
  url.searchParams.set("emptyIcon", resolvedIcon!);

  // Append theming params
  const themingKeys = Object.keys(MATRIX_THEMING_PROP_TO_PARAM) as Array<
    keyof MatrixThemingProps
  >;
  for (const key of themingKeys) {
    const value = props[key];
    if (value !== undefined && value !== null && String(value).length > 0) {
      url.searchParams.set(MATRIX_THEMING_PROP_TO_PARAM[key], String(value));
    }
  }

  return url.toString();
}

/**
 * LegionMatrix – a React component that embeds the Legion
 * Matrix (site-wide notification feed) widget via an iframe.
 *
 * @example
 * ```tsx
 * import { LegionMatrix } from '@legionhandtech/lht-react-widgets';
 *
 * function App() {
 *   return (
 *     <LegionMatrix
 *       baseUrl="https://your-legion-domain.com"
 *       theme="light"
 *       emptyTitle="You're all caught up"
 *       emptyIcon="bell"
 *     />
 *   );
 * }
 * ```
 */
export const LegionMatrix = forwardRef<LegionMatrixHandle, MatrixWidgetProps>(
  function LegionMatrix(props, ref) {
    const {
      theme = "light",
      emptyTitle,
      emptySubtitle,
      accentColor,
      emptyIcon,
      width = "100%",
      height = "400px",
      className,
      style,
      onLoad,
      onError,
      baseUrl,
      ...rest
    } = props;

    const iframeRef = useRef<HTMLIFrameElement>(null);

    useImperativeHandle(
      ref,
      () => ({
        refresh: () => {
          const iframe = iframeRef.current;
          if (iframe?.contentWindow) {
            iframe.contentWindow.location.reload();
          }
        },
      }),
      []
    );

    const src = useMemo(
      () => buildMatrixWidgetUrl(props),
      [
        baseUrl,
        theme,
        emptyTitle,
        emptySubtitle,
        accentColor,
        emptyIcon,
        ...Object.keys(MATRIX_THEMING_PROP_TO_PARAM).map(
          (k) => rest[k as keyof typeof rest]
        ),
      ]
    );

    const handleLoad = useCallback(() => {
      onLoad?.({ widget: "matrix", theme });
    }, [theme, onLoad]);

    const handleError = useCallback(() => {
      onError?.("Failed to load matrix widget");
    }, [onError]);

    const containerStyle: React.CSSProperties = {
      display: "block",
      width,
      backgroundColor: "transparent",
      ...style,
    };

    const iframeStyle: React.CSSProperties = {
      border: "none",
      borderRadius: "8px",
      overflow: "hidden",
      width: "100%",
      height,
    };

    return (
      <div className={className} style={containerStyle}>
        <iframe
          ref={iframeRef}
          src={src}
          title="Matrix Widget"
          width="100%"
          loading="lazy"
          style={iframeStyle}
          onLoad={handleLoad}
          onError={handleError}
        />
      </div>
    );
  }
);

